import * as React from "react";
import { Alert, Badge, Button, Combobox, Label, Spinner, Table } from "@/components/ui";
import { SocialSvg } from "@/components/SocialSvg";
import { CompleteAccount, SocialNetworksId } from "@/interfaces";
import { CoachService, UsersService } from "@/services";
import { AdminServices, ReviewImportResult, ReviewImportRow } from "@/services/bedrock";
import { FileSpreadsheet, Star, Upload } from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

const TEMPLATE = `REVIEW UPLOAD INSTRUCTIONS
Add one row per review under the column headings below. Everything above the headings is ignored. Return the file to SocialCoach as a CSV.

Player Email: Required if Player GUID is empty.
Player GUID: Required if Player Email is empty. If you fill in both they must be the same Player.
Reviewer Name: The name shown on the review.
"Source: IF (Internal Feedback), BE (Birdeye), EX (Experience), SC (SocialCoach), GL (Google), FB (Facebook), or ZL (Zillow)"
"Rating Type: Star, NPS, 10-point, or Text"
"Review Rating: Star is 0 to 5 and can have decimals. NPS and 10-point are whole numbers from 0 to 10. Leave blank for Text."
"Review Date: MM/DD/YYYY, MM/DD/YY, or YYYY-MM-DD"
Review Comment: The review text. Required when Rating Type is Text and optional otherwise.

Player Email,Player GUID,Reviewer Name,Source,Rating Type,Review Rating,Review Date,Review Comment
`;
const MAX_BYTES = 15 * 1024 * 1024;

const SOURCE_LABELS: Record<string, string> = {
  IF: "Internal Feedback",
  BE: "Birdeye",
  EX: "Experience",
  SC: "SocialCoach",
  GL: "Google",
  FB: "Facebook",
  ZL: "Zillow",
};

const RATING_LABELS: Record<string, string> = {
  star: "Star",
  nps: "NPS",
  ten_point: "10-point",
  text: "Text",
};

type RowFilter = "attention" | "inserts" | "updates" | "all";

interface CoachMatch {
  coachId: number;
  label: string;
  detail?: string;
}

function sameCoach(a: CoachMatch, b: CoachMatch) {
  return a.coachId === b.coachId;
}

function accountToCoachMatch(account: CompleteAccount): CoachMatch | null {
  const coachId = Number(account.coach?.id);
  if (!coachId) return null;
  const name =
    account.profile?.fullName || [account.profile?.firstName, account.profile?.lastName].filter(Boolean).join(" ");
  const company = account.coach?.companyName;
  const email = account.user.email;
  const label = name || company || email || `Coach ${coachId}`;
  const detail = [company, email].filter(part => part && part !== label).join(" · ");
  return { coachId, label, detail: detail || undefined };
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function messageFrom(error: unknown) {
  const data = (error as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
  return data?.message || data?.error || "Could not import reviews.";
}

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "review-upload-template.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function selectionKey(coach: CoachMatch | null, file: File | null) {
  if (!coach || !file) return null;
  return `${coach.coachId}:${file.name}:${file.size}:${file.lastModified}`;
}

function outcomeLabel(row: ReviewImportRow, dryRun: boolean) {
  if (row.outcome === "create") return dryRun ? "Insert" : "Created";
  if (row.outcome === "update") return dryRun ? "Update" : "Updated";
  return "Needs attention";
}

export function ReviewUploads() {
  const history = useHistory();
  const location = useLocation();
  const linkedCoachId = React.useMemo(() => {
    const id = Number(new URLSearchParams(location.search).get("coachId"));
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [location.search]);
  const [query, setQuery] = React.useState("");
  const [matches, setMatches] = React.useState<CoachMatch[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [coach, setCoach] = React.useState<CoachMatch | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [result, setResult] = React.useState<ReviewImportResult | null>(null);
  const [resultKey, setResultKey] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<RowFilter>("attention");
  const [busy, setBusy] = React.useState<"check" | "import" | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const requestRef = React.useRef(0);

  React.useEffect(() => {
    if (!linkedCoachId || coach?.coachId === linkedCoachId) return;
    let cancelled = false;
    CoachService.getById(String(linkedCoachId))
      .then(account => {
        if (cancelled) return;
        const match = accountToCoachMatch(account);
        if (match) setCoach(match);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [linkedCoachId, coach?.coachId]);

  React.useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setMatches([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    UsersService.searchAccounts({ searchTerm: term, profileTypes: ["COACH"] }, 0, 16)
      .then(accounts => {
        if (cancelled) return;
        setMatches(accounts.content.flatMap(account => accountToCoachMatch(account) ?? []));
      })
      .catch(() => {
        if (!cancelled) setMatches([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const currentKey = selectionKey(coach, file);

  const chooseFile = (next: File | null) => {
    setError(null);
    if (next && !next.name.toLowerCase().endsWith(".csv")) {
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setError("Choose a CSV file.");
      return;
    }
    if (next && next.size > MAX_BYTES) {
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setError("CSV must be 15 MB or smaller.");
      return;
    }
    setFile(next);
  };

  React.useEffect(() => {
    if (!currentKey || !coach || !file) return;
    const request = ++requestRef.current;
    let cancelled = false;
    setBusy("check");
    setError(null);
    AdminServices.importReviews(coach.coachId, file, true)
      .then(next => {
        if (cancelled || request !== requestRef.current) return;
        setResult(next);
        setResultKey(currentKey);
        setFilter(next.errorCount > 0 ? "attention" : "all");
      })
      .catch(checkError => {
        if (cancelled || request !== requestRef.current) return;
        setResult(null);
        setError(messageFrom(checkError));
      })
      .finally(() => {
        if (!cancelled && request === requestRef.current) setBusy(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentKey, coach, file]);

  const resultMatches = result != null && resultKey === currentKey;
  const readyCount = resultMatches ? result.created + result.updated : 0;

  const importReviews = async () => {
    if (!coach || !file || !resultMatches || result?.dryRun !== true || readyCount === 0) return;
    const request = ++requestRef.current;
    setBusy("import");
    setError(null);
    try {
      const next = await AdminServices.importReviews(coach.coachId, file, false);
      if (request !== requestRef.current) return;
      setResult(next);
      setResultKey(currentKey);
      setFilter(next.errorCount > 0 ? "attention" : "all");
    } catch (importError) {
      if (request !== requestRef.current) return;
      setError(messageFrom(importError));
    } finally {
      if (request === requestRef.current) setBusy(null);
    }
  };

  const coachOptions = coach && !matches.some(match => sameCoach(match, coach)) ? [coach, ...matches] : matches;
  const visibleRows =
    resultMatches && result
      ? result.rows.filter(row => {
          if (filter === "attention") return row.outcome === "error";
          if (filter === "inserts") return row.outcome === "create";
          if (filter === "updates") return row.outcome === "update";
          return true;
        })
      : [];

  const selectCoach = (next: CoachMatch | null) => {
    setQuery("");
    setCoach(next);
    setError(null);
    const params = new URLSearchParams(location.search);
    if (next) params.set("coachId", String(next.coachId));
    else params.delete("coachId");
    const search = params.toString();
    if (search === location.search.replace(/^\?/, "")) return;
    history.replace({ pathname: location.pathname, search });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex max-w-xl flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label>Coach</Label>
          <Combobox<CoachMatch>
            searchable
            showClear
            items={coachOptions}
            value={coach}
            onSearch={setQuery}
            onValueChange={selectCoach}
            isItemEqualToValue={sameCoach}
            itemToStringLabel={item => item.label}
            itemToLabel={item => (
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{item.label}</span>
                {item.detail && <span className="truncate text-xs text-muted-foreground">{item.detail}</span>}
              </span>
            )}
            placeholder="Search coaches..."
            emptyText={searching || query.trim().length >= 2 ? "No matches" : "Type to search"}
            isLoadingMore={searching}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <Label>CSV file</Label>
            <Button type="button" variant="link" className="h-auto px-0" onClick={downloadTemplate}>
              Download template
            </Button>
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={event => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDragOver={event => {
              event.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={event => {
              event.preventDefault();
              setDragOver(false);
              chooseFile(event.dataTransfer.files?.[0] ?? null);
            }}
            className={cn(
              "flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed px-4 py-8 text-center transition-colors outline-none focus-visible:ring-2 focus-visible:ring-amber-400",
              dragOver
                ? "border-amber-400 bg-amber-50"
                : "border-slate-200 bg-gradient-to-b from-white to-slate-50 hover:border-amber-300 hover:bg-amber-50/40"
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={event => {
                chooseFile(event.target.files?.[0] ?? null);
                event.target.value = "";
              }}
            />
            <div className="flex size-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 shadow-sm">
              <FileSpreadsheet className="size-6" />
            </div>
            {file ? (
              <>
                <div className="max-w-full truncate text-sm font-semibold">{file.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)} · Click or drop to replace
                </div>
              </>
            ) : (
              <>
                <div className="text-sm font-semibold">Drop a CSV here</div>
                <div className="text-xs text-muted-foreground">or click to browse</div>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            The template starts with instructions for each column. They are ignored on upload, so leave or delete them.
            {file && !coach ? " Choose a Coach and the file will be checked." : ""}
          </p>
        </div>

        {busy === "check" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="size-4" />
            Checking Players and reviews
          </div>
        )}
        {error && <div className="text-sm text-destructive">{error}</div>}
      </div>

      {resultMatches && result && (
        <div className="flex flex-col gap-3">
          {!result.dryRun && (
            <Alert>
              <Alert.Title>Import complete</Alert.Title>
              <Alert.Description>
                Created {result.created} and updated {result.updated} reviews for {coach?.label}.
                {result.errorCount > 0 ? ` ${result.errorCount} rows were not imported.` : ""}
              </Alert.Description>
            </Alert>
          )}
          <div className="grid max-w-3xl grid-cols-4 gap-3">
            {(
              [
                {
                  id: "attention" as const,
                  label: "Needs attention",
                  count: result.errorCount,
                  idle: "border-rose-100 bg-rose-50/70 text-rose-900 hover:border-rose-300",
                  focused: "border-rose-500 bg-rose-50 text-rose-950 ring-2 ring-rose-400",
                },
                {
                  id: "inserts" as const,
                  label: result.dryRun ? "Inserts" : "Inserted",
                  count: result.created,
                  idle: "border-emerald-100 bg-emerald-50/70 text-emerald-900 hover:border-emerald-300",
                  focused: "border-emerald-500 bg-emerald-50 text-emerald-950 ring-2 ring-emerald-400",
                },
                {
                  id: "updates" as const,
                  label: result.dryRun ? "Updates" : "Updated",
                  count: result.updated,
                  idle: "border-sky-100 bg-sky-50/70 text-sky-900 hover:border-sky-300",
                  focused: "border-sky-500 bg-sky-50 text-sky-950 ring-2 ring-sky-400",
                },
                {
                  id: "all" as const,
                  label: "All",
                  count: result.rowCount,
                  idle: "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300",
                  focused: "border-slate-700 bg-white text-slate-950 ring-2 ring-slate-400",
                },
              ] as const
            ).map(card => (
              <button
                key={card.id}
                type="button"
                aria-pressed={filter === card.id}
                onClick={() => setFilter(card.id)}
                className={cn(
                  "cursor-pointer rounded-xl border px-3 py-2 text-left transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                  filter === card.id ? card.focused : card.idle
                )}
              >
                <div className="text-xs font-medium">{card.label}</div>
                <div className="text-2xl font-semibold">{card.count}</div>
              </button>
            ))}
          </div>
          <div className="max-h-[32rem] overflow-auto rounded-xl border bg-background shadow-sm">
            <Table className="text-sm">
              <Table.Header>
                <Table.Row>
                  <Table.Head>Row</Table.Head>
                  <Table.Head>Player</Table.Head>
                  <Table.Head>Reviewer</Table.Head>
                  <Table.Head>Source</Table.Head>
                  <Table.Head>Rating type</Table.Head>
                  <Table.Head>Rating</Table.Head>
                  <Table.Head>Date</Table.Head>
                  <Table.Head>Comment</Table.Head>
                  <Table.Head>Result</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {visibleRows.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={9} className="text-muted-foreground">
                      Nothing in this view.
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  visibleRows.map(row => (
                    <Table.Row key={row.line} className={row.outcome === "error" ? "bg-rose-50/70" : undefined}>
                      <Table.Cell>{row.line}</Table.Cell>
                      <Table.Cell>
                        <div className="max-w-56">
                          <div className="truncate">{row.playerName || "—"}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {row.playerEmail || row.playerGuid}
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="max-w-40 truncate">{row.reviewerName}</Table.Cell>
                      <Table.Cell>
                        {row.sourceSystem && SOURCE_LABELS[row.sourceSystem] ? (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                            <SocialSvg
                              networkId={row.sourceSystem as SocialNetworksId}
                              size="xs"
                              variant="icon"
                              bgColorClass="bg-transparent"
                            />
                            {SOURCE_LABELS[row.sourceSystem]}
                          </span>
                        ) : (
                          row.sourceSystem
                        )}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        {row.ratingType ? RATING_LABELS[row.ratingType] ?? row.ratingType : null}
                      </Table.Cell>
                      <Table.Cell>
                        {row.rating ? (
                          <span className="inline-flex items-center gap-1">
                            {row.ratingType === "star" && <Star className="size-3.5 fill-amber-400 text-amber-400" />}
                            {row.rating}
                          </span>
                        ) : row.ratingType === "text" ? (
                          <span className="text-muted-foreground">Text</span>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap">{row.reviewDate}</Table.Cell>
                      <Table.Cell className="max-w-xs truncate" title={row.content || undefined}>
                        {row.content}
                      </Table.Cell>
                      <Table.Cell>
                        {row.outcome === "error" ? (
                          <span className="text-destructive">{row.message}</span>
                        ) : (
                          <div className="flex max-w-sm flex-col items-start gap-1">
                            <Badge variant={row.outcome === "create" ? "default" : "secondary"}>
                              {outcomeLabel(row, result.dryRun)}
                            </Badge>
                            {row.outcome === "update" && row.message && (
                              <span className="text-xs text-muted-foreground">{row.message}</span>
                            )}
                          </div>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl border bg-gradient-to-r from-slate-50 to-amber-50 px-4 py-3">
            <p className="text-sm text-muted-foreground">
              {result.dryRun
                ? `${readyCount} ${readyCount === 1 ? "review is" : "reviews are"} ready to import.`
                : "Import complete."}
            </p>
            <Button
              type="button"
              disabled={!result.dryRun || readyCount === 0 || busy !== null}
              onClick={importReviews}
            >
              {busy === "import" ? <Spinner className="size-4" /> : <Upload className="size-4" />}
              {readyCount > 0 ? `Import ${readyCount} ${readyCount === 1 ? "review" : "reviews"}` : "Import reviews"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
